import RouteTemplate from 'ember-route-template';
import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { eq } from '../../helpers/article-type';
import LoadingSpinner from '../../components/loading/spinner';

class GamesIndexComponent extends Component {
  get games() {
    return this.args.model?.games || [];
  }

  get isLoading() {
    return this.args.model?.loading || false;
  }

  get hasError() {
    return this.args.model?.error;
  }

  <template>
    <div class="layout-content-1 layout-item-1 grid-limit padded">
      <div class="section-title-wrap blue">
        <h1 class="section-title large">Games Library</h1>
        <div class="section-title-separator"></div>
        <p class="section-description">Discover and explore our collection of featured games</p>
      </div>

      {{#if this.isLoading}}
        <div class="loading-container">
          <LoadingSpinner @size="large" @message="Loading games..." />
        </div>
      {{else if this.hasError}}
        <div class="error-container">
          <div class="error-message">
            <h3>Unable to load games</h3>
            <p>There was an error loading the games library. Please try again later.</p>
          </div>
        </div>
      {{else if (eq this.games.length 0)}}
        <div class="empty-state">
          <div class="empty-message">
            <h3>No games found</h3>
            <p>Check back later for new games to be added to our library.</p>
          </div>
        </div>
      {{else}}
        <div class="games-masonry">
          {{#each this.games as |game|}}
            <LinkTo @route="games.game" @model={{game.slug}} class="game-card-link">
              <div class="game-card-image">
                {{#if game.image.url}}
                  <img src={{game.image.url}} alt={{game.name}} loading="lazy" />
                {{else}}
                  <div class="game-card-placeholder">
                    <svg class="game-icon" viewBox="0 0 24 24">
                      <path d="M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2M12,4A8,8 0 0,1 20,12A8,8 0 0,1 12,20A8,8 0 0,1 4,12A8,8 0 0,1 12,4M12,6A6,6 0 0,0 6,12A6,6 0 0,0 12,18A6,6 0 0,0 18,12A6,6 0 0,0 12,6Z" />
                    </svg>
                    <span>{{game.name}}</span>
                  </div>
                {{/if}}
                <div class="game-card-overlay">
                  <div class="game-card-content">
                    <h3 class="game-title">{{game.name}}</h3>
                    {{#if game.description}}
                      <p class="game-description">{{game.description}}</p>
                    {{/if}}
                    <div class="game-meta">
                      {{#if game.releaseDate}}
                        <span class="release-date">Released {{game.releaseDate}}</span>
                      {{/if}}
                      {{#if game.platforms}}
                        <div class="platforms">
                          {{#each game.platforms as |platform|}}
                            <span class="platform-tag">{{platform}}</span>
                          {{/each}}
                        </div>
                      {{/if}}
                    </div>
                  </div>
                </div>
              </div>
            </LinkTo>
          {{/each}}
        </div>
      {{/if}}
    </div>

    <style scoped>
      .section-description {
        margin-top: 0.5rem;
        color: #666;
        font-size: 1.1rem;
        line-height: 1.5;
      }

      .loading-container,
      .error-container,
      .empty-state {
        display: flex;
        justify-content: center;
        align-items: center;
        min-height: 400px;
        text-align: center;
      }

      .error-message,
      .empty-message {
        background: #f8f9fa;
        border: 1px solid #dee2e6;
        border-radius: 12px;
        padding: 2rem;
        max-width: 500px;
      }

      .error-message h3,
      .empty-message h3 {
        color: #495057;
        margin: 0 0 1rem 0;
      }

      .error-message p,
      .empty-message p {
        color: #6c757d;
        margin: 0;
      }

      .games-masonry {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 1.5rem;
        margin-top: 2rem;
      }

      .game-card-link {
        position: relative;
        display: block;
        border-radius: 12px;
        overflow: hidden;
        transition: transform 0.3s ease, box-shadow 0.3s ease;
        text-decoration: none;
        background: #fff;
        box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
      }

      .game-card-link:hover {
        transform: translateY(-4px);
        box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
        text-decoration: none;
      }

      .game-card-image {
        position: relative;
        width: 100%;
        height: 200px;
        overflow: hidden;
      }

      .game-card-image img {
        width: 100%;
        height: 100%;
        object-fit: cover;
        transition: transform 0.3s ease;
      }

      .game-card-link:hover .game-card-image img {
        transform: scale(1.05);
      }

      .game-card-placeholder {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        width: 100%;
        height: 100%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        color: white;
        text-align: center;
      }

      .game-icon {
        width: 48px;
        height: 48px;
        fill: currentColor;
        margin-bottom: 0.5rem;
        opacity: 0.8;
      }

      .game-card-placeholder span {
        font-weight: 600;
        font-size: 0.875rem;
      }

      .game-card-overlay {
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.4) 50%, transparent 100%);
        opacity: 0;
        transition: opacity 0.3s ease;
        display: flex;
        align-items: flex-end;
        padding: 1.5rem;
      }

      .game-card-link:hover .game-card-overlay {
        opacity: 1;
      }

      .game-card-content {
        width: 100%;
        color: white;
      }

      .game-title {
        margin: 0 0 0.5rem 0;
        font-size: 1.25rem;
        font-weight: 700;
        color: white;
      }

      .game-description {
        margin: 0 0 1rem 0;
        font-size: 0.875rem;
        line-height: 1.4;
        opacity: 0.9;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
      }

      .game-meta {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
      }

      .release-date {
        font-size: 0.75rem;
        opacity: 0.8;
      }

      .platforms {
        display: flex;
        flex-wrap: wrap;
        gap: 0.25rem;
      }

      .platform-tag {
        background: rgba(255, 255, 255, 0.2);
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.625rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }

      /* Responsive design */
      @media (max-width: 768px) {
        .games-masonry {
          grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
          gap: 1rem;
        }

        .game-card-image {
          height: 160px;
        }

        .game-card-overlay {
          opacity: 1;
          background: linear-gradient(to top, rgba(0, 0, 0, 0.8) 0%, transparent 60%);
        }

        .game-title {
          font-size: 1.125rem;
        }
      }

      @media (max-width: 480px) {
        .games-masonry {
          grid-template-columns: 1fr;
        }
      }
    </style>
  </template>
}

export default RouteTemplate(GamesIndexComponent);
